import { Fragment, useMemo, useState } from "react"
import { DateTime } from "luxon"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { ChartPie, ChevronDown } from "lucide-react"
import { DateRange, GetSmsThreadStatsParams } from "../../../types"
import { useGetSmsThreadStatsQuery } from "../../../redux/queries"
import { useAppSelector } from "../../hooks"
import { selectSelectedBrandID } from "../../../redux/slices/conversations"
import { useUserRoles } from "../../hooks"
import DaysFilter from "../days-filter"
import { ConversationsStatsSkeleton } from "../skeletons"
import NoData from "../no-data"
import { getConversationStatsChartOptions } from "./options"

interface ConversationMetrics {
  dashboardVisits: number
  noReply: number
  scheduledCalls: number
  replies: number
}

const ConversationsStats = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [range, setRange] = useState<DateRange>(7)

  const selectedBrandID = useAppSelector(selectSelectedBrandID)
  const { isBankAdmin, isSuperAdmin, user } = useUserRoles()

  // Build API query params based on user role
  const queryParams: GetSmsThreadStatsParams = useMemo(() => {
    const params: GetSmsThreadStatsParams = {}
    const now = DateTime.now()

    // Determine timestamp range (API expects milliseconds Unix timestamp)
    // Note: API defaults to last 7 days if no timestamp_min is provided
    if (range === 1000) {
      // For "All time", use org/brand creation date or fallback to 5 years ago
      let createdDate: DateTime | null = null

      if (user?.org?.created) {
        // Try parsing org creation date (could be Unix timestamp or ISO format)
        const timestamp = Number(user.org.created)
        if (!isNaN(timestamp)) {
          // It's a Unix timestamp (in seconds)
          createdDate = DateTime.fromSeconds(timestamp)
        } else {
          // Try ISO format
          createdDate = DateTime.fromISO(user.org.created)
          // If ISO parsing fails, try SQL format
          if (!createdDate.isValid) {
            createdDate = DateTime.fromSQL(user.org.created)
          }
        }
      }

      // If we couldn't parse the date or it doesn't exist, fallback to 5 years ago
      if (!createdDate || !createdDate.isValid) {
        createdDate = now.minus({ years: 5 })
        console.log("Using fallback date (5 years ago) for All Time range")
      } else {
        console.log(
          `Using org created date for All Time: ${createdDate.toISO()} (from ${
            user?.org?.created
          })`
        )
      }

      params.timestamp_min = createdDate.startOf("day").toMillis()
      // params.timestamp_max = now.endOf("day").toMillis()
    } else {
      // For specific ranges (7 or 30 days), calculate cutoff date at start of day
      const cutoff = now.minus({ days: range }).startOf("day")
      params.timestamp_min = cutoff.toMillis()
      // params.timestamp_max = now.endOf("day").toMillis()
    }

    // Determine scope based on user role
    // if (isSuperAdmin) {
    //   // Super admin: use brand_id if selected, otherwise show all
    //   if (selectedBrandID) {
    //     params.brand_id = selectedBrandID
    //   }
    // } else if (isBankAdmin) {
    //   // Bank admin: use org_id
    //   if (user?.org?.id) {
    //     params.org_id = user.org.id
    //   }
    //   // If brand is selected from brand selector, also include brand_id
    //   if (selectedBrandID) {
    //     params.brand_id = selectedBrandID
    //   }
    // } else {
    //   // Loan officer: use loid
    //   if (user?.id) {
    //     params.loid = user.id
    //   }
    //   if (user?.org?.brands?.[0]?.id) {
    //     params.brand_id = user.org.brands[0].id
    //   }
    // }

    // Loan officer: use loid

    if (!isSuperAdmin && !isBankAdmin) {
      if (user?.id) {
        params.loid = user.id
      }
    }
    if (selectedBrandID) {
      params.brand_id = selectedBrandID
    } else if (user?.org?.brands?.[0]?.id) {
      params.brand_id = user.org.brands[0].id
    }

    return params
  }, [range, isSuperAdmin, isBankAdmin, selectedBrandID, user])

  // Fetch stats from API
  const {
    data: statsData,
    isLoading,
    isFetching,
    isError,
    error
  } = useGetSmsThreadStatsQuery(queryParams, {
    skip: !isOpen // Only fetch when the stats section is open
  })

  // Process API response into metrics and chart data
  const { metrics, chartData, totalChats, containsData } = useMemo(() => {
    // Default empty state
    const emptyMetrics: ConversationMetrics = {
      dashboardVisits: 0,
      noReply: 0,
      scheduledCalls: 0,
      replies: 0
    }

    const emptyChartData: [string, number][] = [
      ["No Reply", 0],
      ["Replies", 0],
      ["Scheduled Calls", 0],
      ["Dashboard Visits", 0]
    ]

    // If loading, error, or no data, return empty state
    if (!statsData || isError || statsData.status === "error") {
      return {
        metrics: emptyMetrics,
        chartData: emptyChartData,
        totalChats: 0,
        containsData: false
      }
    }

    // Extract metrics from API response
    const dashboardVisits = statsData.threads_with_a_dashboard_link_click ?? 0
    const noReply = statsData.threads_without_a_reply ?? 0
    const scheduledCalls = statsData.threads_with_a_scheduled_call ?? 0
    const replies = statsData.threads_with_a_reply ?? 0
    const total = statsData.total_threads ?? 0

    const metricsData: ConversationMetrics = {
      dashboardVisits,
      noReply,
      scheduledCalls,
      replies
    }

    // Prepare chart data for donut chart
    const chartDataArray: [string, number][] = [
      ["No Reply", noReply],
      ["Replies", replies],
      ["Scheduled Calls", scheduledCalls],
      ["Dashboard Visits", dashboardVisits]
    ]

    return {
      metrics: metricsData,
      chartData: chartDataArray,
      totalChats: total,
      containsData: total > 0
    }
  }, [statsData, isError])

  return (
    <div className="border-t border-neutral-200">
      <button
        onClick={() => setIsOpen(v => !v)}
        className="
          w-full flex items-center 
          py-3 px-4
          text-base font-normal text-base-dark
        "
      >
        <div className="flex items-center mr-auto">
          <ChartPie size={20} className="mr-2" /> <span>Stats</span>
        </div>
        <ChevronDown
          size={24}
          className={`
              transition-transform duration-200
              ${isOpen ? "rotate-180" : "rotate-0"}
            `}
        />
      </button>

      {isOpen && (
        <div className="p-4">
          <div className="flex justify-center">
            <DaysFilter
              className="scale-90 origin-center"
              range={range}
              visibleRanges={[7, 30, 60]}
              handleUpdateRange={setRange}
            />
          </div>
          {isLoading || isFetching ? (
            <ConversationsStatsSkeleton className="mt-6" />
          ) : !containsData ? (
            <NoData />
          ) : (
            <Fragment>
              <div className="bg-white rounded">
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getConversationStatsChartOptions(
                    chartData,
                    totalChats
                  )}
                />
              </div>

              {/* Custom Legend */}
              <div className="flex gap-8 mb-2">
                <div className="flex flex-col gap-6 shrink-0">
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-normal leading-5 tracking-[0.48px] uppercase text-base-dark">
                      Dashboard Visits
                    </p>
                    <p className="text-lg font-extrabold leading-6 text-base-dark">
                      {metrics.dashboardVisits.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-normal leading-5 tracking-[0.48px] uppercase text-base-dark">
                      Scheduled Calls
                    </p>
                    <p className="text-lg font-extrabold leading-6 text-base-dark">
                      {metrics.scheduledCalls.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-6 shrink-0">
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-normal leading-5 tracking-[0.48px] uppercase text-base-dark">
                      No Reply
                    </p>
                    <p className="text-lg font-extrabold leading-6 text-base-dark">
                      {metrics.noReply.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-normal leading-5 tracking-[0.48px] uppercase text-base-dark">
                      Replies
                    </p>
                    <p className="text-lg font-extrabold leading-6 text-base-dark">
                      {metrics.replies.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </Fragment>
          )}
        </div>
      )}
    </div>
  )
}

export default ConversationsStats
